<template>
  <div class="billing-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 mb-4">Billing</h1>
        </v-col>
      </v-row>

      <!-- Credit Balance Card -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card outlined class="mb-6">
            <v-card-title class="account-card-title">
              <v-icon left color="primary">mdi-credit-card-outline</v-icon>
              {{ usage ? 'Your usage' : 'Credit Balance' }}
            </v-card-title>
            <v-card-text v-if="loading">
              <v-skeleton-loader type="text@3" />
            </v-card-text>
            <v-card-text v-else-if="creditBalance || usage">
              <account-usage-summary v-if="usage" :usage="usage" @retry="refreshUsage"/>
              <template v-if="!usage">
              <!-- Genuinely-free users don't see the hidden signup grant;
                   the balance appears once they've bought a pack
                   (showCreditInfo) or subscribed. -->
              <div class="d-flex align-center">
                <div
                  v-if="showCreditInfo"
                  class="display-1 font-weight-bold mr-2"
                >
                  {{ formatCredits(creditBalance.balance) }}
                </div>
                <!-- Only meaningful to users who can actually buy packs -->
                <v-chip v-if="canBuyCredits" color="info" small outlined>
                  Top-up rate: $1 = {{ topupRate }} credits
                </v-chip>
              </div>
              <div v-if="showCreditInfo" class="subtitle-1 mt-2">
                Available credits: {{ formatCredits(creditBalance.balance) }}
                <span
                  v-if="creditEquivalent(creditBalance.balance)"
                  class="body-2 text--secondary"
                >
                  · {{ creditEquivalent(creditBalance.balance) }}
                </span>
              </div>
              <div v-if="showCreditInfo && creditBalance.reserved_credits" class="body-2 mt-1">
                Reserved for work: {{ formatCredits(creditBalance.reserved_credits) }}
              </div>
              <div v-if="showCreditInfo" class="subtitle-1 mt-2">
                Credits Used: {{ formatCredits(creditBalance.used) }}
              </div>
              <div class="body-2 mt-1">Plan: {{ formatPlanName(creditBalance.plan) }}</div>
              <div class="body-2 mt-1" v-if="creditBalance.renewal_date">
                Next Renewal: {{ formatDate(creditBalance.renewal_date) }}
              </div>

              <v-alert type="info" text dense class="mt-3">
                {{ productName }} credits are SaaS usage units that pay for AI
                processing. They are not money, stored value, a
                wallet balance, or transferable value, and cannot be withdrawn,
                transferred, resold, or redeemed for cash.
              </v-alert>

              </template>
              <v-divider class="my-4"></v-divider>

              <!-- Credit packs are a subscriber perk on this app
                   (config.TOPUPS_PAID_ONLY), and guests can never buy -
                   point them at a subscription instead of a button the
                   backend would refuse with a 403. -->
              <div v-if="$config.TOPUPS_ENABLED === false"><p>{{ $config.BILLING_NO_TOPUPS_COPY || 'Usage is included in your subscription. Top-ups are not available.' }}</p><v-btn color="primary" to="/pricing">See plans</v-btn></div>
              <div v-else-if="!canBuyCredits">
                <v-alert type="info" text dense class="mb-3">
                  Credit top-ups are available on paid plans. Subscribe for a
                  monthly allowance, then buy extra credits whenever you need
                  them.
                </v-alert>
                <v-btn color="primary" :to="subscribeCtaTarget">
                  <v-icon left>mdi-arrow-up-bold</v-icon>
                  See plans
                </v-btn>
              </div>
              <!-- Non-subscribers can buy packs at the pack rate; subscribers
                   get the better per-credit rate. -->
              <div v-else-if="isFreePlan">
                <v-alert type="info" text dense class="mb-3">
                  Credit packs are available on the free plan at
                  {{ freeTopupRate }} credits per $1.
                  <strong
                    >Subscribers get {{ paidTopupRate }} credits per $1</strong
                  >
                  (~40% more) plus higher limits -
                  <router-link to="/subscribe">see plans</router-link>.
                </v-alert>
                <v-btn color="primary" @click="openPurchaseCreditsDialog">
                  Purchase Credits
                </v-btn>
              </div>
              <div v-else>
                <v-btn color="primary" @click="openPurchaseCreditsDialog">
                  Purchase Additional Credits
                </v-btn>
              </div>
            </v-card-text>
            <v-card-text v-else>
              <p>No credit balance information available.</p>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Subscription Summary Card -->
        <v-col cols="12" md="6">
          <v-card outlined class="mb-6">
            <v-card-title class="account-card-title">
              <v-icon left color="primary">mdi-card-account-details</v-icon>
              Subscription Summary
            </v-card-title>
            <v-card-text v-if="loading">
              <v-skeleton-loader type="text@3" />
            </v-card-text>
            <v-card-text v-else-if="subscription">
              <div class="subtitle-1 font-weight-bold">
                Current Plan:
                {{ formatPlanName(subscription.subscription_plan) }}
              </div>
              <div class="body-1 mt-2">
                Status:
                <v-chip
                  :color="getStatusColor(subscription.subscription_status)"
                  small
                  text-color="white"
                >
                    {{ usage && usage.planStatus ? usage.planStatus : formatStatus(subscription.subscription_status) }}
                </v-chip>
              </div>
              <div
                class="body-1 mt-2"
                v-if="subscription.subscription_start_date"
              >
                Started: {{ formatDate(subscription.subscription_start_date) }}
              </div>
              <div
                class="body-1 mt-2"
                v-if="subscription.subscription_end_date"
              >
                {{ usage ? usage.renewalLabel : 'Renews' }}: {{ formatDate(subscription.subscription_end_date) }}
              </div>

              <!-- Only a paid plan has anything to manage. Secondary, so the
                   balance card keeps the page's one primary action. -->
              <template v-if="hasPaidSubscription">
                <v-divider class="my-4"></v-divider>

                <v-btn color="primary" outlined to="/subscription">
                  Manage Subscription
                </v-btn>
              </template>
            </v-card-text>
            <v-card-text v-else>
              <p>No subscription information available.</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Upgrade upsell for lower-tier users -->
      <v-row v-if="isLowerTier && !usage">
        <v-col cols="12">
          <v-card outlined class="upgrade-upsell-card mb-6">
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon left color="primary">mdi-rocket-launch-outline</v-icon>
                <span class="text-h6">{{ upsell.title || "Unlock more with Pro & Ultra" }}</span>
              </div>
              <p class="body-2 grey--text mb-4">
                {{ upsell.text || "Upgrade for a bigger allowance and the features your current plan doesn't include." }}
              </p>
              <v-row dense class="mb-3">
                <v-col
                  v-for="perk in displayUpgradePerks"
                  :key="perk.title"
                  cols="12"
                  sm="6"
                  md="3"
                >
                  <div class="d-flex align-start">
                    <v-icon small color="success" class="mr-2 mt-1"
                      >mdi-check-circle</v-icon
                    >
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ perk.title }}
                      </div>
                      <div class="caption grey--text">{{ perk.desc }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-btn color="primary" depressed to="/subscribe">
                <v-icon left>mdi-arrow-up-bold</v-icon>
                {{ upsell.cta || "Compare plans & upgrade" }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Credit History. Hidden for genuinely-free users along with the
           balance (the debit rows would reveal the hidden signup grant), and
           in apps without credits, whose ledger holds no activity of theirs. -->
      <v-row v-if="showCreditInfo">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">{{ usage ? usageHistoryTitle : 'Credit Transaction History' }}</h2>
        </v-col>
      </v-row>

      <v-row v-if="showCreditInfo">
        <v-col cols="12">
          <v-card outlined>
            <v-data-table
              :headers="creditHistoryHeaders"
              :items="creditHistory"
              :loading="loading"
              :items-per-page="10"
              :no-data-text="historyEmptyText"
              class="elevation-0"
            >
              <template v-slot:item.amount="{ item }">
                <span
                  :class="{
                    'green--text': item.amount > 0,
                    'red--text': item.amount < 0
                  }"
                >
                  {{ item.amount > 0 ? "+" : "" }}{{ item.amount }}
                </span>
              </template>
              <template v-slot:item.transaction_type="{ item }">
                <v-chip
                  small
                  :color="getTransactionTypeColor(item.transaction_type)"
                  text-color="white"
                >
                  {{ formatTransactionType(item.transaction_type) }}
                </v-chip>
              </template>
              <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Purchase Credits Dialog -->
    <v-dialog v-model="purchaseCreditsDialog" max-width="500">
      <v-card>
        <v-card-title>Purchase Additional Credits</v-card-title>
        <v-card-text>
          <v-alert type="info" text dense class="mb-4">
            <strong>Pricing:</strong> $1 = {{ topupRate }} credits
            <span v-if="creditEquivalent(topupRate)">
              ({{ creditEquivalent(topupRate) }})
            </span>
          </v-alert>

          <v-form ref="purchaseForm" v-model="purchaseFormValid">
            <v-text-field
              v-model.number="purchaseAmount"
              label="Number of Credits"
              type="number"
              hint="Enter the number of credits you want to purchase"
              persistent-hint
              :rules="[
                v => !!v || 'Amount is required',
                v => v > 0 || 'Amount must be greater than 0'
              ]"
              required
            ></v-text-field>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <v-col cols="6">
                <div class="subtitle-2 grey--text">Credits:</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ purchaseAmount || 0 }}
                </div>
                <div
                  v-if="creditEquivalent(purchaseAmount)"
                  class="caption text--secondary"
                >
                  {{ creditEquivalent(purchaseAmount) }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="subtitle-2 grey--text">Cost:</div>
                <div class="subtitle-1 font-weight-bold">
                  ${{ calculatePurchaseTotal() }}
                </div>
              </v-col>
            </v-row>

            <div class="caption grey--text mt-2">
              Credits will be added to your account immediately after payment is
              processed.
            </div>
            <div v-if="guaranteeInfo" class="caption grey--text mt-1">
              {{ guaranteeInfo.short }}: {{ guaranteeInfo.long }}
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="purchaseCreditsDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!purchaseFormValid"
            @click="purchaseCredits"
          >
            Purchase
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" :color="snackbarColor" :timeout="5000">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { formatPlanName } from "@shared/utils/planLabels";
import { guarantee } from "@shared/utils/guarantee";
import AccountUsageSummary from "@shared/components/AccountUsageSummary.vue";
import {accountUsage,refreshAccountUsage} from "@shared/utils/accountUsage";
import { serverMessage } from "@shared/utils/memberFunnel";
import { usageHistoryTitle, historyEmptyText } from "@shared/utils/productWording";
// Seam: config.js is per-app - see the note in services/api.js.
import { PRODUCT_NAME } from "@/config";

// Credit top-up rates (credits per $1). Mirrors the backend's
// FREE_TOPUP_CREDITS_PER_DOLLAR / TOPUP_CREDITS_PER_DOLLAR settings; the
// public config / plans payloads do not currently expose these, so they live
// here as named constants until the backend surfaces them.
const TOPUP_CREDITS_PER_DOLLAR_FREE = 7;
const TOPUP_CREDITS_PER_DOLLAR_PAID = 10;

export default {
  components:{AccountUsageSummary},
  name: "BillingView",
  data() {
    return {
      purchaseCreditsDialog: false,
      purchaseAmount: 100,
      purchaseFormValid: false,
      errorSnackbar: false,
      errorMessage: "",
      snackbarColor: "error",
      creditHistoryHeaders: [
        { text: "Date", value: "created_at" },
        { text: "Amount", value: "amount" },
        { text: "Type", value: "transaction_type" },
        { text: "Description", value: "description" }
      ],
      upgradePerks: [
        // First perk is plan-dependent: where top-ups are a subscriber perk
        // (TOPUPS_PAID_ONLY) the rate comparison is meaningless, because
        // non-subscribers cannot buy at any rate. See displayUpgradePerks.
        {
          title: "Better credit rate",
          desc: "Subscribers get more credits per dollar on top-ups"
        },
        {
          title: "Bigger credit allotment",
          desc: "A larger monthly credit allowance"
        },
        {
          title: "Higher limits",
          desc: "More headroom for heavier workloads"
        }
      ]
    };
  },
  computed: {
    usage(){return accountUsage(this.$store,this.$config);},
    usageHistoryTitle() { return usageHistoryTitle(); },
    historyEmptyText() { return historyEmptyText(this.usage); },
    ...mapGetters([
      "currentSubscription",
      "creditBalance",
      "creditHistory",
      "loading",
      "error",
      "showCreditInfo"
    ]),
    subscription() {
      return this.currentSubscription;
    },
    // A paid plan to manage: neither Free nor a paid-only site's member
    // without a plan yet (stored as "free" too).
    hasPaidSubscription() {
      var plan = this.subscription && this.subscription.subscription_plan;
      return !!plan && String(plan).toLowerCase() !== "free";
    },
    productName() {
      return PRODUCT_NAME;
    },
    // Per-app seam: config.CREDIT_EQUIVALENT renders credit amounts in the
    // user's own units ("≈ N conversions"); apps without it see no change.
    creditEquivalentFn() {
      const fn = this.$config && this.$config.CREDIT_EQUIVALENT;
      return typeof fn === "function" ? fn : null;
    },
    // Credits per $1 at the current plan's top-up rate.
    topupRate() {
      return this.isFreePlan
        ? TOPUP_CREDITS_PER_DOLLAR_FREE
        : TOPUP_CREDITS_PER_DOLLAR_PAID;
    },
    freeTopupRate() {
      return TOPUP_CREDITS_PER_DOLLAR_FREE;
    },
    paidTopupRate() {
      return TOPUP_CREDITS_PER_DOLLAR_PAID;
    },
    isFreePlan() {
      return (
        this.subscription &&
        (this.subscription.subscription_plan.toLowerCase() === "free" ||
          (this.creditBalance &&
            this.creditBalance.plan.toLowerCase() === "free"))
      );
    },
    isLowerTier() {
      if (this.loading) return false;
      var plan =
        this.subscription && this.subscription.subscription_plan
          ? String(this.subscription.subscription_plan).toLowerCase()
          : "free";
      return plan === "free" || plan === "basic";
    },
    // Apps where credit packs are a subscriber perk (config.TOPUPS_PAID_ONLY,
    // mirroring the backend's SITE_<SLUG>_TOPUPS_PAID_ONLY gate). Anonymous
    // trial guests can never buy on any app - the backend refuses them
    // unconditionally, so offering the button would only produce a 403.
    isAnonymousUser() {
      var user = this.$store.getters.currentUser;
      return !!(user && user.is_anonymous);
    },
    canBuyCredits() {
      if (this.$config && this.$config.TOPUPS_ENABLED === false) return false;
      if (this.isAnonymousUser) return false;
      if (this.$config && this.$config.TOPUPS_PAID_ONLY) {
        return !this.isFreePlan;
      }
      return true;
    },
    // Where the "subscribe first" CTA should land: guests must register
    // before they can hold a subscription.
    subscribeCtaTarget() {
      return this.isAnonymousUser
        ? { path: "/register", query: { next: "/subscribe" } }
        : { path: "/subscribe" };
    },
    // An app may word the upsell for its own plans ($config.BILLING_UPSELL =
    // {title, text, cta, perks}); the reference copy names Pro and Ultra.
    upsell() {
      return (this.$config && this.$config.BILLING_UPSELL) || {};
    },
    guaranteeInfo() {
      return guarantee();
    },
    displayUpgradePerks() {
      if (this.upsell.perks) return this.upsell.perks;
      if (this.$config && this.$config.TOPUPS_ENABLED === false) return this.upgradePerks.slice(1);
      if (!(this.$config && this.$config.TOPUPS_PAID_ONLY)) {
        return this.upgradePerks;
      }
      // "Better credit rate" would imply non-subscribers can already buy.
      return [
        {
          title: "Credit top-ups",
          desc: "Buy extra credits whenever you need them"
        }
      ].concat(this.upgradePerks.slice(1));
    }
  },
  methods: {
    refreshUsage(){return refreshAccountUsage(this.$store,this.$config);},
    creditEquivalent(credits) {
      return this.creditEquivalentFn
        ? this.creditEquivalentFn(Number(credits) || 0)
        : null;
    },
    ...mapActions([
      "fetchSubscription",
      "fetchCreditBalance",
      "fetchCreditHistory",
      "fetchCurrentUser",
      "purchaseCredits"
    ]),
    refreshAfterPurchase() {
      this.fetchCurrentUser();
      this.fetchCreditBalance();
      this.fetchCreditHistory();
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    },
    formatStatus(status) {
      if (!status) return "Unknown";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatPlanName,
    formatTransactionType(type) {
      if (!type) return "Unknown";
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
    formatCredits(value) {
      if (value === null || value === undefined) return "0";
      return parseFloat(Number(value).toFixed(2));
    },
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "past_due":
          return "warning";
        case "canceled":
        case "canceling":
          return "error";
        default:
          return "grey";
      }
    },
    getTransactionTypeColor(type) {
      switch (type) {
        case "consumption":
          return "red";
        case "renewal":
          return "green";
        case "adjustment":
          return "blue";
        case "purchase":
          return "purple";
        default:
          return "grey";
      }
    },
    calculatePurchaseTotal() {
      // Subscribers buy at 10 credits/$1; free-plan users at 7 credits/$1
      // (see FREE_TOPUP_CREDITS_PER_DOLLAR on the backend).
      const amount = this.purchaseAmount || 0;
      return (amount / this.topupRate).toFixed(2);
    },
    openPurchaseCreditsDialog() {
      this.purchaseCreditsDialog = true;
    },
    purchaseCredits() {
      if (!this.$refs.purchaseForm.validate()) return;

      // Show loading state
      this.errorMessage = "Creating checkout session...";
      this.snackbarColor = "info";
      this.errorSnackbar = true;

      this.$store
        .dispatch("purchaseCredits", this.purchaseAmount)
        .then(response => {
          this.purchaseCreditsDialog = false;
          this.errorSnackbar = false;

          // Redirect to the active hosted checkout
          if (response && response.checkout_url) {
            console.log("Redirecting to checkout:", response.checkout_url);
            window.location.href = response.checkout_url;
          } else {
            console.error("No checkout URL returned:", response);
            this.errorMessage =
              "Failed to create checkout session. Please try again.";
            this.snackbarColor = "error";
            this.errorSnackbar = true;
          }
        })
        .catch(error => {
          console.error("Error purchasing credits:", error);
          this.errorMessage = serverMessage(error, "Failed to purchase credits. Please try again.");
          this.snackbarColor = "error";
          this.errorSnackbar = true;
        });
    },

    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("success") === "true") {
        const amount = urlParams.get("amount");
        // Refresh account state now, then again after the webhook has had
        // time to land (the redirect can beat it) - this flips the free-lock
        // and toolbar credits without a manual reload.
        this.refreshAfterPurchase();
        setTimeout(() => this.refreshAfterPurchase(), 5000);
        setTimeout(() => this.refreshAfterPurchase(), 15000);

        // Calculate dollar amount
        const dollarAmount = (parseInt(amount) / this.topupRate).toFixed(2);
        this.errorMessage = `Your payment completed for ${amount} credits ($${dollarAmount}). Credits will appear once the payment webhook sync finishes.`;
        this.snackbarColor = "success";
        this.errorSnackbar = true;

        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else if (urlParams.get("canceled") === "true") {
        this.errorMessage = "Credit purchase was canceled.";
        this.snackbarColor = "info";
        this.errorSnackbar = true;

        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  },
  created() {
    // Fetch billing data
    this.fetchSubscription();
    this.fetchCreditBalance();
    this.fetchCreditHistory();

    // Check URL parameters after checkout redirect
    this.checkUrlParams();
  }
};
</script>

<style scoped>
.billing-view {
  padding-bottom: 2rem;
}

.upgrade-upsell-card {
  border-color: var(--border-cyan-20, rgba(56, 189, 248, 0.25));
}
</style>
